import { Icon } from "@mdi/react";
import { useMemo, useRef } from "react";

import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { mdiClose } from "@mdi/js";
import { Button, InputNumber } from "antd";
import { FORECAST_KEY } from "constants/settings.constants";
import _debounce from "lodash/debounce";
import { selectSelectedTypeWell, useForecastStore } from "stores/forecast";
import styled from "styled-components";
import getColumnLabelWithContext from "utils/column/getColumnLabelWithContext";

import useColumns from "hooks/useColumns";
import { useEvaUnitCore } from "hooks/useEvaUnitCore";
import useUserUnit from "hooks/useUserUnit";

import { WellData } from "models/UserArpsModel";

import GroupBy from "components/groupBy";
import { useSelectedProjectPermissions } from "components/project/projects/hooks";

import { EntityKind } from "../../../../../models/entityKind";

export interface TypeWellNormalizationParameterProps {
  index: number;
  validateTypeWellData: (wellData: WellData) => boolean;
  onChange?: (value?: WellData) => void;
}

export function TypeWellNormalizationParameter({
  index,
  validateTypeWellData,
  onChange
}: TypeWellNormalizationParameterProps) {
  const projectPermissions = useSelectedProjectPermissions();
  const selectedTypeWell = useForecastStore(selectSelectedTypeWell);
  const setSelectedTypeWell = useForecastStore((s) => s.setSelectedTypeWell);
  const wellData = selectedTypeWell?.wellData;
  const parameterData = wellData?.AdditionalData[index];

  const changeDebounceRef = useRef(
    _debounce((fn) => {
      fn && fn();
    }, 600)
  );

  const columns = useColumns();

  let property = wellData.AdditionalData[index].field;
  if (property?.endsWith(FORECAST_KEY)) {
    property = property.replace(`.${FORECAST_KEY}`, "");
  }

  let normalizationField = columns.find((c) => property === c.property);

  let fieldTitle = "Select Field";
  if (normalizationField) {
    const normalizationFieldWithContext = getColumnLabelWithContext(
      normalizationField,
      false
    );

    fieldTitle = normalizationFieldWithContext?.fullContextTitle;
  } else {
    normalizationField = columns.find((c) => c.property === "Frac_Data.Stages");
  }

  const unitType = useUserUnit();
  const { isReady, convertUnitToUnit, getTargetUnit } = useEvaUnitCore();

  const sourceUnit = normalizationField?.units?.[0];
  const targetUnit = useMemo(() => {
    if (!sourceUnit || !isReady) return sourceUnit;
    return getTargetUnit(sourceUnit, unitType);
  }, [sourceUnit, unitType, isReady, getTargetUnit]);

  const convertToDisplay = (valueInSource: number): number => {
    if (!valueInSource || !isReady || !sourceUnit || !targetUnit) return valueInSource;
    if (sourceUnit === targetUnit) return valueInSource;
    return convertUnitToUnit(sourceUnit, targetUnit, valueInSource, 2);
  };

  const convertToSource = (valueInTarget: number): number => {
    if (!valueInTarget || !isReady || !sourceUnit || !targetUnit) return valueInTarget;
    if (sourceUnit === targetUnit) return valueInTarget;
    return convertUnitToUnit(targetUnit, sourceUnit, valueInTarget, 6);
  };

  const displayValue = useMemo(
    () => convertToDisplay(parameterData?.value),
    [parameterData?.value, sourceUnit, targetUnit, isReady]
  );

  const changeAdditionalNormalizationField = (field, value) => {
    const newAdditionalData = [...wellData.AdditionalData];
    newAdditionalData[index] = { field, value };
    const updatedWellData: WellData = {
      ...wellData,
      AdditionalData: newAdditionalData
    };
    setSelectedTypeWell({
      ...selectedTypeWell,
      wellData: {
        ...updatedWellData
      }
    });
    if (validateTypeWellData(updatedWellData)) {
      changeDebounceRef.current(() => onChange && onChange(updatedWellData));
    }
  };

  const removeAdditionalNormalizationField = () => {
    // eslint-disable-next-line prefer-const
    let newAdditionalData = [...wellData.AdditionalData];
    newAdditionalData.splice(index, 1);
    const updatedWellData: WellData = {
      ...wellData,
      AdditionalData: newAdditionalData
    };
    setSelectedTypeWell({
      ...selectedTypeWell,
      wellData: {
        ...updatedWellData
      }
    });
    if (validateTypeWellData(updatedWellData)) {
      changeDebounceRef.current(() => onChange && onChange(updatedWellData));
    }
  };

  const canEditParams = projectPermissions.canEditTypeWells;
  return (
    <>
      <GroupBy
        value={normalizationField}
        dataTypeFilters={["Number", "Integer"]}
        entityKind={EntityKind.Well}
        placement="rightTop"
        isDisabled={!canEditParams}
        onChange={(gb) =>
          changeAdditionalNormalizationField(gb.property, convertToSource(displayValue))
        }>
        <StyledButton block disabled={!canEditParams}>
          {fieldTitle}
          <KeyboardArrowRightIcon style={{ fontSize: 24 }} />
        </StyledButton>
      </GroupBy>

      <StyledNumberInput
        value={displayValue}
        disabled={!canEditParams}
        controls={false}
        onChange={(value) =>
          changeAdditionalNormalizationField(parameterData.field, convertToSource(value))
        }
        onDragStart={(e) => e.preventDefault()}
        onDrop={(e) => e.preventDefault()}
      />

      <RemoveButtonWrapper
        disabled={!canEditParams}
        onClick={removeAdditionalNormalizationField}>
        <Icon path={mdiClose} size={"20px"} />
      </RemoveButtonWrapper>
    </>
  );
}

const StyledButton = styled(Button)`
  width: 100%;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  height: 30px;
  text-align: left;

  & > span {
    max-width: fit-content;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledNumberInput = styled(InputNumber)`
  width: 100%;
  height: 30px;
  background-color: #ebf8ef;
  border-radius: var(--border-radius);
`;

const RemoveButtonWrapper = styled(Button)`
  --ant-primary-color: #a2aaad;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  box-shadow: none;
  margin-right: 5px;
  cursor: pointer;
  color: #a2aaad;
  background: none;
  padding: 0;

  &:hover {
    color: var(--color-primary);
  }

  &:disabled {
    background: none;

    &:hover {
      background: none;
    }
  }

  .icon-toggle {
    color: rgba(194, 194, 194, 0.8);
    opacity: 0;
  }
`;
